import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'PaperBrainz';
const SITE_URL = 'https://paperbrainz.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
const OG_IMAGE_ALT = 'PaperBrainz - AI-Powered Customer Support Chatbot';
const INDEX_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

// react-snap emits directories (build/pricing/index.html), so Apache's DirectorySlash
// 301s /pricing -> /pricing/. Every URL we publish about ourselves — canonical, og:url,
// hreflang, sitemap — has to name the slashed form, otherwise each one redirects before
// it resolves and Google files the pre-redirect URL under "Page with redirect".
// Exported so scripts/generate-sitemap.js builds identical URLs from the same rule.
export const canonicalPath = (p) => {
  if (!p || p === '/') return '/';
  return p.endsWith('/') ? p : `${p}/`;
};

function SEO({
  title,
  description,
  path = '',
  noindex = false,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  jsonLd = null,
  lang = 'en',
  alternates = null, // array of { lang: 'en'|'hy', path: '/...' } for hreflang
}) {
  // Homepage titles already carry the brand, so they are used as-is. That includes the
  // localised homepages (/hy), which would otherwise render "PaperBrainz — ... - PaperBrainz".
  const isHome = path === '/' || path === '' || /^\/(hy)$/.test(path);
  const fullTitle = isHome ? title : `${title} - ${SITE_NAME}`;
  const canonical = `${SITE_URL}${canonicalPath(path)}`;

  // defer={false} is load-bearing for prerendering, not a preference. react-helmet-async
  // defers every head mutation to requestAnimationFrame by default; react-snap serializes
  // the DOM before that callback runs, so each prerendered page shipped the static
  // index.html head — homepage title, homepage og:url, no canonical, no per-page JSON-LD.
  // Committing synchronously puts the tags in the snapshot. Do not remove.
  return (
    <Helmet defer={false}>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Always emitted, so exactly one robots tag reaches the page. index.html used to
          carry a static "index, follow" as well; on a noindex route that left two
          contradictory tags in the same head and relied on Google picking the stricter. */}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : INDEX_ROBOTS} />

      {/* hreflang alternates */}
      {alternates && alternates.map((a) => (
        <link key={a.lang} rel="alternate" hrefLang={a.lang} href={`${SITE_URL}${canonicalPath(a.path)}`} />
      ))}
      {alternates && (
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${canonicalPath(alternates.find(a => a.lang === 'en')?.path || '/')}`} />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      {/* Dimensions describe DEFAULT_OG_IMAGE specifically, so they are only emitted for it.
          They must also stay adjacent to og:image — OG sub-properties bind to the preceding
          og:image, which is why these live here and not in index.html's static head. */}
      {ogImage === DEFAULT_OG_IMAGE && <meta property="og:image:width" content="1200" />}
      {ogImage === DEFAULT_OG_IMAGE && <meta property="og:image:height" content="630" />}
      {ogImage === DEFAULT_OG_IMAGE && <meta property="og:image:alt" content={OG_IMAGE_ALT} />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={lang === 'hy' ? 'hy_AM' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {ogImage === DEFAULT_OG_IMAGE && <meta name="twitter:image:alt" content={OG_IMAGE_ALT} />}

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export default SEO;
